import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CameraDto, CameraProtocol, CreateCameraInput } from '@parking/shared';
import { api } from '../api/client';
import { useAuthStore } from '../store/authStore';
import { AppModal } from './AppModal';
import { ParkingDataTable } from './ParkingDataTable';
import { StatusBadge } from './StatusBadge';

type CameraForm = {
  parkingId: string;
  code: string;
  name: string;
  host: string;
  port: string;
  username: string;
  password: string;
  protocol: CameraProtocol;
  streamUrl: string;
  alprEnabled: boolean;
  isActive: boolean;
};

const emptyForm = (parkingId = ''): CameraForm => ({
  parkingId,
  code: '',
  name: '',
  host: '',
  port: '554',
  username: '',
  password: '',
  protocol: 'RTSP',
  streamUrl: '',
  alprEnabled: true,
  isActive: true,
});

type Props = {
  canWrite: boolean;
};

export function SettingsCamerasPanel({ canWrite }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canReadParkings = useAuthStore((s) => s.hasPermission('parkings:read'));
  const [dialog, setDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CameraForm>(emptyForm());
  const [formError, setFormError] = useState<string | null>(null);

  const parkings = useQuery({
    queryKey: ['parkings'],
    queryFn: () => api.listParkings(),
    enabled: canReadParkings || canWrite,
  });
  const cameras = useQuery({
    queryKey: ['settings-cameras'],
    queryFn: () => api.listCameras(),
  });

  const parkingRows = parkings.data?.data ?? [];
  const rows = cameras.data?.data ?? [];

  const closeDialog = () => {
    setDialog(false);
    setEditingId(null);
    setFormError(null);
    setForm(emptyForm(parkingRows[0]?.id ?? ''));
  };

  const openCreate = () => {
    setEditingId(null);
    setFormError(null);
    setForm(emptyForm(parkingRows[0]?.id ?? ''));
    setDialog(true);
  };

  const openEdit = (c: CameraDto) => {
    setEditingId(c.id);
    setFormError(null);
    setForm({
      parkingId: c.parkingId,
      code: c.code,
      name: c.name,
      host: c.host ?? '',
      port: String(c.port ?? 554),
      username: c.username ?? '',
      password: '',
      protocol: c.protocol,
      streamUrl: c.streamUrl ?? '',
      alprEnabled: c.alprEnabled,
      isActive: c.isActive,
    });
    setDialog(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const portNum = Number.parseInt(form.port, 10);
      const payload: CreateCameraInput = {
        parkingId: form.parkingId,
        code: form.code.trim(),
        name: form.name.trim(),
        host: form.host.trim() || null,
        port: Number.isFinite(portNum) ? portNum : 554,
        username: form.username.trim() || null,
        password: form.password.trim() || null,
        protocol: form.protocol,
        streamUrl: form.streamUrl.trim() || null,
        alprEnabled: form.alprEnabled,
        isActive: form.isActive,
      };
      if (editingId) {
        const { parkingId, ...rest } = payload;
        return api.updateCamera(editingId, {
          ...rest,
          parkingId,
          password: form.password.trim() ? form.password.trim() : undefined,
        });
      }
      return api.createCamera(payload);
    },
    onSuccess: async () => {
      closeDialog();
      await qc.invalidateQueries({ queryKey: ['settings-cameras'] });
      await qc.invalidateQueries({ queryKey: ['cameras'] });
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteCamera(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['settings-cameras'] });
      await qc.invalidateQueries({ queryKey: ['cameras'] });
    },
  });

  const columns = [
    t('settings.cameras.code'),
    t('settings.cameras.name'),
    t('settings.cameras.parking'),
    t('settings.cameras.host'),
    t('settings.cameras.protocol'),
    t('settings.cameras.status'),
    ...(canWrite ? [t('common.actions')] : []),
  ];

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <p className="text-muted small mb-0">{t('settings.cameras.hint')}</p>
        {canWrite && (
          <button type="button" className="btn btn-primary btn-sm" onClick={openCreate}>
            {t('settings.cameras.create')}
          </button>
        )}
      </div>

      {cameras.isError && (
        <div className="alert alert-danger">{(cameras.error as Error).message}</div>
      )}

      <ParkingDataTable
        storageKey="settings-cameras"
        columns={columns}
        refreshKey={rows.map((c) => `${c.id}:${c.isActive}:${c.host}:${c.protocol}`).join('|')}
        actionsColumn={canWrite}
        galleryItems={rows.map((c) => ({
          id: c.id,
          icon: 'bi-camera-video',
          iconTone: c.isActive ? ('success' as const) : ('secondary' as const),
          title: c.code,
          subtitle: c.name,
          meta: `${c.parkingCode ?? '—'} · ${c.host ?? '—'} · ${c.protocol}`,
          badge: (
            <StatusBadge tone={c.isActive ? 'success' : 'secondary'}>
              {c.isActive ? t('common.activeOn') : t('common.activeOff')}
            </StatusBadge>
          ),
          actions: canWrite ? (
            <>
              <button
                type="button"
                className="btn btn-sm btn-icon btn-ghost-primary"
                aria-label="edit"
                onClick={() => openEdit(c)}
              >
                <i className="bi bi-pencil" />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-icon btn-ghost-danger"
                aria-label="delete"
                onClick={() => {
                  if (window.confirm(t('settings.cameras.confirmDelete'))) {
                    remove.mutate(c.id);
                  }
                }}
              >
                <i className="bi bi-trash" />
              </button>
            </>
          ) : undefined,
        }))}
      >
        {rows.map((c) => (
          <tr key={c.id}>
            <td className="fw-semibold">{c.code}</td>
            <td>{c.name}</td>
            <td>
              {c.parkingCode ?? '—'}
              {c.parkingName ? ` · ${c.parkingName}` : ''}
            </td>
            <td>
              {c.host ?? '—'}
              {c.port != null ? `:${c.port}` : ''}
            </td>
            <td>{c.protocol}</td>
            <td>
              <StatusBadge tone={c.isActive ? 'success' : 'secondary'}>
                {c.isActive ? t('common.activeOn') : t('common.activeOff')}
              </StatusBadge>
            </td>
            {canWrite && (
              <td>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-primary"
                  aria-label="edit"
                  onClick={() => openEdit(c)}
                >
                  <i className="bi bi-pencil" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-danger"
                  aria-label="delete"
                  onClick={() => {
                    if (window.confirm(t('settings.cameras.confirmDelete'))) {
                      remove.mutate(c.id);
                    }
                  }}
                >
                  <i className="bi bi-trash" />
                </button>
              </td>
            )}
          </tr>
        ))}
      </ParkingDataTable>

      <AppModal
        open={dialog}
        onClose={closeDialog}
        title={editingId ? t('settings.cameras.edit') : t('settings.cameras.create')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={closeDialog}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={save.isPending || !form.parkingId || !form.code.trim() || !form.name.trim()}
              onClick={() => save.mutate()}
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {formError && <div className="alert alert-danger mb-0">{formError}</div>}
          <div>
            <label className="form-label">{t('settings.cameras.parking')}</label>
            <select
              className="form-select"
              value={form.parkingId}
              onChange={(e) => setForm((f) => ({ ...f, parkingId: e.target.value }))}
            >
              <option value="">{t('settings.cameras.selectParking')}</option>
              {parkingRows.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">{t('settings.cameras.code')}</label>
            <input
              className="form-control"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('settings.cameras.name')}</label>
            <input
              className="form-control"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('settings.cameras.protocol')}</label>
            <select
              className="form-select"
              value={form.protocol}
              onChange={(e) =>
                setForm((f) => ({ ...f, protocol: e.target.value as CameraProtocol }))
              }
            >
              {(['RTSP', 'HTTP', 'HTTPS', 'ONVIF'] as const).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">{t('settings.cameras.host')}</label>
            <input
              className="form-control"
              placeholder="192.168.1.50"
              value={form.host}
              onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('settings.cameras.port')}</label>
            <input
              className="form-control"
              type="number"
              min={1}
              max={65535}
              value={form.port}
              onChange={(e) => setForm((f) => ({ ...f, port: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('settings.cameras.username')}</label>
            <input
              className="form-control"
              autoComplete="off"
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('settings.cameras.password')}</label>
            <input
              className="form-control"
              type="password"
              autoComplete="new-password"
              value={form.password}
              placeholder={
                editingId
                  ? t('settings.cameras.passwordKeep')
                  : t('settings.cameras.passwordPlaceholder')
              }
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            />
          </div>
          <div className="modal-form__full">
            <label className="form-label">{t('settings.cameras.streamUrl')}</label>
            <input
              className="form-control"
              placeholder="rtsp://192.168.1.50:554/stream1"
              value={form.streamUrl}
              onChange={(e) => setForm((f) => ({ ...f, streamUrl: e.target.value }))}
            />
            <div className="form-text">{t('settings.cameras.streamUrlHint')}</div>
          </div>
          <div className="modal-form__full d-flex flex-wrap gap-4">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="cam-active"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="cam-active">
                {t('settings.cameras.isActive')}
              </label>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="cam-alpr"
                checked={form.alprEnabled}
                onChange={(e) => setForm((f) => ({ ...f, alprEnabled: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="cam-alpr">
                {t('settings.cameras.alprEnabled')}
              </label>
            </div>
          </div>
        </div>
      </AppModal>
    </>
  );
}
